<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import draggable from 'vuedraggable'
import { lookup } from "mime-types";

import adminAPI from '@/services/api';
import { useNotificationStore, useClaimStore } from '@/store';
import { typeTranslations } from '@/utils/config/constants/documents-types';
import { openBoxFile } from '../../utils/open-box-folder';

import BaseButton from '../ui/buttons/BaseButton.vue';
import FileInput from "../files/FileInput.vue";
import FilePreviewModal from "../files/FilePreviewModal.vue";
import AlertModal from "@/components/ui/modals/AlertModal.vue";
import { PaperClipIcon, InformationCircleIcon, DocumentPlusIcon, DocumentMagnifyingGlassIcon, TrashIcon, CloudIcon }
  from '@heroicons/vue/24/outline';
  import SelectDocumentType from '../files/SelectDocumentType.vue';

import type { LoadedFile } from '../files/types';
import type { File, ExpenseClaimDocument, RetrieveClaimWithRelationsResponse, ClaimDocuments } from '@/interfaces/data-contracts';
import { computedAsync } from '@vueuse/core';
import BoxFilePicker from '../Box/BoxFilePicker.vue';
import { useAuth0 } from '@auth0/auth0-vue';
import { getUserRoles } from '@/utils/current-user';

const props = defineProps<{
  claim: RetrieveClaimWithRelationsResponse;
}>();
const emits = defineEmits(["update"]);
const user = ref<any>(useAuth0().user.value);

const claim = computed(() => props.claim);
const files = computed(() => claim.value.claimDocuments);
const filesIn = ref<typeof claim.value.claimDocuments>([]);
const newFiles = ref<LoadedFile[]>([]);

const selectedBoxFile = ref();
const selectedBoxFileDocumentType = ref();

const modalAddDocument = ref(false);
const previewedFile = ref<{ data: string, file: File } | null>();

const drag = ref(false);
const transitionOptions = computed(() => { return { animation: 200, disabled: false, ghostClass: "ghost", }; });

const boxToken = computedAsync(() => {
  if (getUserRoles().includes("UPCLAIM_ADMIN") || getUserRoles().includes("LAWYER")) {
    return adminAPI.getBoxToken();
  }
}, null, { lazy: true })

async function removeClaimDocument(claimDocumentId: string) {
  const { success } = await adminAPI.deleteClaimDocument(claimDocumentId);
  if (success) {
    emits("update");
    useNotificationStore().emitNotification({
      title: "Document supprimé avec succès",
      type: "success",
    });
  }
}

async function previewDocument(file: File) {
  const data = await adminAPI.retrieveFile(file.id)
  if (data) previewedFile.value = { data, file }
}

function closeModalAddDocument() {
  modalAddDocument.value = false;
  newFiles.value = [];
}

async function addClaimDocument(files: LoadedFile[]) {
  if (!(files?.length || selectedBoxFile)) {
    useNotificationStore().emitNotification({ title: "Aucun document sélectionné" })
    return;
  };
  const waitNotification = useNotificationStore().emitNotification({
    type: "wait",
    title: "Document en cours d'envoi"
  })
  console.log(files)
  try {
    // <!-- @TODO Simplify this code : Issue is different logics between Box and Local file selection  -->
    if (files.length) {
      for (const { file, content, documentType } of files) {
        const axiosResponse = await adminAPI.addClaimDocument(props.claim.id, {
          name: file.name,
          document: content,
          mimeType: file.type,
          claimDocumentType: documentType || "OTHER"
        });
      }
    } else {
      const [file] = selectedBoxFile.value;
      console.log("BoxFile:", file, lookup(file.name))
      await adminAPI.addClaimDocument(props.claim.id, {
          name: file.name,
          boxFileId: file.id,
          mimeType: lookup(file.name) || "application/pdf",
          claimDocumentType: selectedBoxFileDocumentType.value
        });
    }

    useNotificationStore().emitNotification({
      title: "Document ajouté avec succès",
      type: "success",
    });
    emits("update");
    closeModalAddDocument();
    await useClaimStore().refreshClaim();
  } catch (e) {

  } finally {
    waitNotification.duration = 0;
  }
}

const tooltipFile = (document: ExpenseClaimDocument) => "Ajouté le " +
  format(new Date(document.createdAt), "d MMMM 'à' HH'h'mm", { locale: fr })
</script>
<template>
  <FilePreviewModal v-show="previewedFile?.data" @close="previewedFile = null" @cancel="previewedFile = null" :file="(previewedFile?.file as any)"
    :data="previewedFile?.data" />
  <AlertModal :open="modalAddDocument" @close="closeModalAddDocument" @cancel="closeModalAddDocument"
    @confirm="addClaimDocument(newFiles)" title="Ajouter un document"
    message="Sélectionner un document à ajouter au dossier." confirmMessage="Envoyer"
    :cancelHandler="() => { modalAddDocument = false; newFiles = [] }" type="info">

    <div v-permission="['UPCLAIM_ADMIN', 'LAWYER']" v-if="boxToken?.accessToken">
        <BoxFilePicker :access-token="boxToken.accessToken" :folder-id="claim.boxFolderId"
          @choose="e => selectedBoxFile = e"
          @cancel="selectedBoxFile = null"
        />
    </div>
    <FileInput @files-updated="(files) => newFiles = files" />
    <div class="py-2">
      <SelectDocumentType v-if="newFiles.length" @selection-update="(type) => newFiles[0].documentType = type" />
      <SelectDocumentType v-else-if="selectedBoxFile" @selection-update="(type) => selectedBoxFileDocumentType = type" />
    </div>
  </AlertModal>

  <div class="py-4">
    <div>
      <draggable group="claim-documents"
          v-bind="transitionOptions"
          @start="drag = true"
          @end="drag = false"
          :list="files" item-key="id" role="list"
          class="w-full col-span-3 mb-5 bg-white rounded-md shadow-sm"
          :class="{ 'border border-gray-300 divide-y divide-gray-100': files.length }"
      >
        <template #item="{ element, index }: { element: ClaimDocuments[number], index: number }">
          <li class="flex items-center justify-between py-4 pl-4 pr-5 text-sm leading-6 list-group-item">
            <!-- <pre>{{ element }}</pre> -->
            <div class="flex items-center flex-1 w-0 cursor-pointer" @click="previewDocument(element.file)">
              <PaperClipIcon class="flex-shrink-0 w-5 h-5 text-gray-400" aria-hidden="true" />
              <div class="flex flex-1 min-w-0 gap-2 ml-4">
                <span class="font-medium truncate">
                  {{ element.documentType.type === "OTHER" ? element.file.name : typeTranslations[element.documentType.type] }}
                </span>
              </div>
            </div>
            <div class="flex flex-shrink-0 ml-4 space-x-4">
              <button type="button" v-tooltip="tooltipFile(element as any)" @click="previewDocument(element.file)"
                class="font-medium text-blue-600 rounded-md hover:text-blue-500">
                <DocumentMagnifyingGlassIcon class="w-5 h-5" />
              </button>
              <span  v-permission="['UPCLAIM_ADMIN', 'LAWYER']" class="text-gray-200" aria-hidden="true">|</span>
              <button type="button" class="font-medium text-gray-900 rounded-md hover:text-gray-800"
                @click="removeClaimDocument(element.id)"
                 v-permission="['UPCLAIM_ADMIN', 'LAWYER']"
              >
                <TrashIcon class="w-5 h-5" />
              </button>
            </div>
          </li>
        </template>
        <template #footer>
          <div v-if="!files.length"  role="list" class="w-full col-span-3 mb-5 bg-white border border-gray-300 border-dotted divide-y divide-gray-100 rounded-md shadow-sm">
            <li class="flex items-center justify-between py-4 pl-4 pr-5 text-sm leading-6">
              <div class="flex items-center flex-1 w-0">
                <InformationCircleIcon class="flex-shrink-0 w-5 h-5 ml-2 text-gray-400" aria-hidden="true" />
                <div class="flex flex-1 min-w-0 gap-2 ml-4">
                  <span class="flex items-center font-medium truncate">
                    Aucun fichier pour l'instant
                  </span>
                  <div class="ml-auto">
                    <BaseButton @click="modalAddDocument = true" text="Ajouter" />
                  </div>
                </div>
              </div>
            </li>
          </div>
        </template>
      </draggable>
    </div>
    <BaseButton v-if="files.length" @click="modalAddDocument = true" text="Ajouter un document" />
  </div>
</template>
<style>
.ghost {
  opacity: 0.5;
  background: #c8ebfb;
}

.not-draggable {
  cursor: no-drop;
}
</style>